import { orderTypeLabel } from '../orderTypeLabel.js';
import { esc } from '../escape.js';

const PREVIEW_COUNT = 8;

const fmtTime = iso =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

class KDSView {
  _list          = document.getElementById('openOrdersList');
  _viewAllBtn    = document.getElementById('openOrdersViewAll');
  _expanded      = false;
  _modalBackdrop = document.getElementById('allOrdersBackdrop');
  _modalList     = document.getElementById('allOrdersModalList');
  _modalCount    = document.getElementById('allOrdersModalCount');
  _modalOpen     = false;

  renderQueue(queue) {
    this.closeStatusPopover(); // rows re-render; the popover's anchor is gone
    if (queue.length === 0) {
      this._list.innerHTML = `
        <li class="oq-empty-state">
          <span class="oq-empty-icon" aria-hidden="true">
            <svg class="oq-empty-bunny" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="42" height="42">
              <path d="M9.3 10.5C8 7 8 3.6 9.6 2c1 2.6 1.4 5.6 1.4 8.6"/>
              <path d="M14.7 10.5C16 7 16 3.6 14.4 2c-1 2.6-1.4 5.6-1.4 8.6"/>
              <circle cx="12" cy="15" r="5.2"/>
              <circle cx="10.1" cy="14.2" r="0.55" fill="currentColor" stroke="none"/>
              <circle cx="13.9" cy="14.2" r="0.55" fill="currentColor" stroke="none"/>
              <circle cx="12" cy="15.7" r="0.45" fill="currentColor" stroke="none"/>
              <path d="M11.1 16.9c.5.6 1.3.6 1.8 0"/>
            </svg>
          </span>
          <span class="oq-empty-title">All caught up</span>
          <span class="oq-empty-subtitle">New orders will hop in the moment they arrive</span>
        </li>`;
      this._viewAllBtn.classList.add('hidden');
      this._expanded = false;
    } else {
      this._list.innerHTML = queue
        .map((order, i) => this._rowMarkup(order, i + 1, i >= PREVIEW_COUNT && !this._expanded))
        .join('');

      if (queue.length > PREVIEW_COUNT) {
        this._viewAllBtn.classList.remove('hidden');
        this._viewAllBtn.textContent = this._expanded
          ? 'Show Less'
          : `View All (${queue.length})`;
      } else {
        this._viewAllBtn.classList.add('hidden');
        this._expanded = false;
      }
    }

    if (this._modalOpen) this._renderModalList(queue);
  }

  _renderModalList(queue) {
    if (this._modalCount) {
      this._modalCount.textContent = queue.length > 0
        ? `${queue.length} order${queue.length !== 1 ? 's' : ''}`
        : '';
    }
    if (queue.length === 0) {
      this._modalList.innerHTML = '<li class="oq-empty">No active orders.</li>';
      return;  // modal uses compact empty state
    }
    this._modalList.innerHTML = queue
      .map((order, i) => this._rowMarkup(order, i + 1, false))
      .join('');
  }

  openModal(queue) {
    this._modalOpen = true;
    this._modalBackdrop.classList.remove('hidden');
    this._renderModalList(queue);
  }

  closeModal() {
    this._modalOpen = false;
    this._modalBackdrop.classList.add('hidden');
  }

  _addHandlerOpenModal(getQueue) {
    document.getElementById('openOrdersExpandBtn')?.addEventListener('click', () => {
      this.openModal(getQueue());
    });
  }

  _addHandlerModalClose() {
    document.getElementById('allOrdersCloseBtn')?.addEventListener('click', () => this.closeModal());
    this._modalBackdrop?.addEventListener('click', e => {
      if (e.target === this._modalBackdrop) this.closeModal();
    });
  }

  // ── "Mark as…" mini-modal ──────────────────────────────────────────────────
  // ONE shared floating popover on <body> (same singleton pattern as
  // tooltip.js) so it never clips inside the queue panel or the All Orders
  // modal. Anchored to the clicked button, viewport-clamped, flips above when
  // there's no room below.
  _popover = null;
  _onReadyChoice = null;
  _onDoneChoice = null;
  _onUnreadyChoice = null;

  _ensurePopover() {
    if (this._popover) return this._popover;
    const el = document.createElement('div');
    el.className = 'status-popover';
    el.setAttribute('role', 'menu');
    el.innerHTML = `
      <p class="status-popover-title" data-pop-title></p>
      <button class="status-popover-ready" type="button" role="menuitem">🛎️ Ready for Pickup</button>
      <button class="status-popover-unready hidden" type="button" role="menuitem">↩ Back to Preparing</button>
      <button class="status-popover-done" type="button" role="menuitem">✓ Done</button>`;
    document.body.appendChild(el);
    el.querySelector('.status-popover-ready').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onReadyChoice) this._onReadyChoice(id);
    });
    el.querySelector('.status-popover-unready').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onUnreadyChoice) this._onUnreadyChoice(id);
    });
    el.querySelector('.status-popover-done').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onDoneChoice) this._onDoneChoice(id);
    });
    this._popover = el;
    return el;
  }

  _openStatusPopover(btn) {
    const el = this._ensurePopover();
    const isReady = btn.dataset.state === 'ready';
    el.dataset.orderId = btn.dataset.orderId;
    el.querySelector('[data-pop-title]').textContent = `Update order #${btn.dataset.ticket}`;
    // State-aware choices: preparing → Ready/Done; ready → Back to Preparing/Done
    // (Done takes the green when it's the natural next step).
    el.querySelector('.status-popover-ready').classList.toggle('hidden', isReady);
    el.querySelector('.status-popover-unready').classList.toggle('hidden', !isReady);
    el.classList.toggle('status-popover--ready', isReady);
    el.classList.add('status-popover--open');
    const r = btn.getBoundingClientRect();
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    const left = Math.min(Math.max(8, r.right - pw), window.innerWidth - pw - 8);
    let top = r.bottom + 6;
    if (top + ph > window.innerHeight - 8) top = r.top - ph - 6;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  closeStatusPopover() {
    if (!this._popover) return;
    this._popover.classList.remove('status-popover--open');
    delete this._popover.dataset.orderId;
  }

  // One delegated listener per list covers every row action: the "Mark as…"
  // mini-modal trigger, the direct Mark Done on ready rows, and the cashier's
  // "back to Preparing" revert.
  _wireActions(root, onDone, onReady, onUnready) {
    this._onReadyChoice = onReady;
    this._onDoneChoice = onDone;
    this._onUnreadyChoice = onUnready;
    root?.addEventListener('click', e => {
      const statusBtn = e.target.closest('.oq-status-btn');
      if (statusBtn) {
        const isOpenForThis = this._popover?.classList.contains('status-popover--open')
          && this._popover.dataset.orderId === statusBtn.dataset.orderId;
        if (isOpenForThis) this.closeStatusPopover();
        else this._openStatusPopover(statusBtn);
        return;
      }
      const done = e.target.closest('.oq-done-btn');
      if (done) return onDone(done.dataset.orderId);
      const unready = e.target.closest('.oq-unready-btn');
      if (unready) return onUnready(unready.dataset.orderId);
    });
  }

  _addHandlerActions(onDone, onReady, onUnready) {
    this._wireActions(this._list, onDone, onReady, onUnready);
  }

  _addHandlerModalActions(onDone, onReady, onUnready) {
    this._wireActions(this._modalList, onDone, onReady, onUnready);
  }

  // Outside click, Escape, scroll, or resize dismisses the mini-modal (its
  // anchor may have moved or gone).
  _addHandlerChooserDismiss() {
    document.addEventListener('click', e => {
      if (e.target.closest('.status-popover') || e.target.closest('.oq-status-btn')) return;
      this.closeStatusPopover();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeStatusPopover();
    });
    document.addEventListener('scroll', () => this.closeStatusPopover(), true);
    window.addEventListener('resize', () => this.closeStatusPopover());
  }

  _buildItemPreview(items) {
    const parts = items.map(it => {
      const qty = it.quantity ?? 1;
      let label = it.itemName;
      if (qty > 1) label += ` ×${qty}`;
      const firstVariant = it.selectedVariants?.[0];
      if (firstVariant?.optionName) label += ` (${firstVariant.optionName})`;
      return label;
    });
    const text = parts.join(' · ');
    return text.length > 55 ? text.slice(0, 52) + '…' : text;
  }

  _rowMarkup(order, num, hidden) {
    const orderTypeEnabled = localStorage.getItem('pointbunny_order_type_enabled') !== 'false';
    const typeLabel = (!orderTypeEnabled || !order.orderType) ? '' : orderTypeLabel(order.orderType);
    const preview = this._buildItemPreview(order.items);
    const isReady = !!order.readyAt;
    // Ready orders count from when they became ready ("how long has this sat
    // uncollected"); preparing orders count from when they were placed.
    const elapsed = Math.floor((Date.now() - (isReady ? order.readyAt : order.startedAt)) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    const waitStr = isReady ? `Ready ${timeStr} ago` : `${timeStr} waiting`;

    return `
      <li class="oq-row${hidden ? ' oq-row--hidden' : ''}" data-order-id="${order.id}">
        <div class="oq-left">
          <span class="oq-order-num">#${order.ticketNumber ?? num}${typeLabel ? ` <span class="oq-type">${typeLabel}</span>` : ''}</span>
          <span class="oq-items">${esc(preview)}</span>
          <span class="oq-waiting" data-waiting-id="${order.id}">${waitStr}</span>
        </div>
        <div class="oq-right">
          <span class="oq-badge ${isReady ? 'oq-badge--ready' : 'oq-badge--preparing'}" data-badge-id="${order.id}">${isReady ? '🛎️ Ready for Pickup' : 'Preparing'}</span>
          <div class="oq-actions">
            <button class="oq-status-btn" data-order-id="${order.id}" data-ticket="${order.ticketNumber ?? num}" data-state="${isReady ? 'ready' : 'preparing'}" type="button">Update Status</button>
          </div>
        </div>
      </li>`;
  }

  updateTimers(queue, now, yellowThreshold, redThreshold) {
    const roots = this._modalOpen
      ? [this._list, this._modalList]
      : [this._list];

    queue.forEach(order => {
      // Urgency always escalates on the order's TOTAL age - a ready order
      // sitting uncollected keeps aging toward Delayed/Urgent (deliberate).
      const elapsed  = Math.floor((now - order.startedAt) / 1000);
      const isReady  = !!order.readyAt;
      const isWarn   = elapsed >= yellowThreshold && elapsed < redThreshold;
      const isUrgent = elapsed >= redThreshold;
      const cls = isReady
        ? `oq-badge oq-badge--ready${isUrgent ? ' oq-badge--ready-urgent' : isWarn ? ' oq-badge--ready-warn' : ''}`
        : `oq-badge ${isUrgent ? 'oq-badge--urgent' : isWarn ? 'oq-badge--warn' : 'oq-badge--preparing'}`;
      const text = isReady
        ? (isUrgent ? '🚨 Ready for Pickup' : isWarn ? '⏰ Ready for Pickup' : '🛎️ Ready for Pickup')
        : (isUrgent ? '🚨 Urgent' : isWarn ? '⏰ Delayed' : 'Preparing');
      // The waiting label counts from ready-time for ready orders (how long
      // it's sat uncollected), from order-time otherwise.
      const shown    = Math.floor((now - (isReady ? order.readyAt : order.startedAt)) / 1000);
      const mins     = Math.floor(shown / 60);
      const secs     = shown % 60;
      const timeStr  = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      const waitStr  = isReady ? `Ready ${timeStr} ago` : `${timeStr} waiting`;

      roots.forEach(root => {
        const badge   = root.querySelector(`.oq-badge[data-badge-id="${order.id}"]`);
        const row     = root.querySelector(`.oq-row[data-order-id="${order.id}"]`);
        const waiting = root.querySelector(`.oq-waiting[data-waiting-id="${order.id}"]`);
        if (!badge || !row) return;
        badge.className = cls;
        badge.textContent = text;
        row.classList.toggle('oq-row--ready',  isReady && !isWarn && !isUrgent);
        row.classList.toggle('oq-row--warn',   isWarn);
        row.classList.toggle('oq-row--urgent', isUrgent);
        if (waiting) waiting.textContent = waitStr;
      });
    });
  }

  playNewOrderSound() {
    try {
      const ctx = new AudioContext();
      const frequencies = [523.25, 659.25, 783.99];
      frequencies.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.07);
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + i * 0.07 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.07 + 0.35);
        osc.start(ctx.currentTime + i * 0.07);
        osc.stop(ctx.currentTime + i * 0.07 + 0.35);
      });
    } catch (_) {}
  }

  _addHandlerViewAll() {
    this._viewAllBtn.addEventListener('click', () => {
      this._expanded = !this._expanded;
      Array.from(this._list.querySelectorAll('.oq-row')).forEach((row, i) => {
        row.classList.toggle('oq-row--hidden', !this._expanded && i >= PREVIEW_COUNT);
      });
      this._viewAllBtn.textContent = this._expanded ? 'Show Less' : `View All`;
    });
  }

  showUndoToast(order, onUndo, durationMs) {
    const label = order.ticketNumber ? `#${order.ticketNumber}` : 'Order';
    const el = document.createElement('div');
    el.className = 'toast toast--undo';
    let remaining = Math.ceil(durationMs / 1000);

    el.innerHTML = `
      <span class="toast-undo-msg">${label} marked done</span>
      <span class="toast-undo-countdown">${remaining}s</span>
      <button class="toast-undo-btn" type="button">Undo</button>`;
    el.querySelector('.toast-undo-btn').addEventListener('click', onUndo, { once: true });

    let container = document.getElementById('undoToastContainer');
    if (!container) {
      container = document.createElement('div');
      container.id = 'undoToastContainer';
      container.className = 'undo-toast-container';
      document.body.appendChild(container);
    }
    container.appendChild(el);
    void el.offsetHeight;
    el.classList.add('toast--visible');

    const countdownInterval = setInterval(() => {
      remaining--;
      const cd = el.querySelector('.toast-undo-countdown');
      if (cd) cd.textContent = `${remaining}s`;
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
      el.classList.remove('toast--visible');
      el.addEventListener('transitionend', () => {
        el.remove();
        const c = document.getElementById('undoToastContainer');
        if (c && c.children.length === 0) c.remove();
      }, { once: true });
    };
  }
}

export default new KDSView();
